import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuCircleCheck, LuCircle, LuChevronRight, LuUserPlus } from 'react-icons/lu';
import api, { crearUsuario } from '../../services/api';
import ModalShell from '../ui/ModalShell';

// Empresa con portal (28-oct-2026): todo lo que tiene Modo Agua, para cualquier cliente, en una lista
// de pasos: tarifa mensual por tramos (cierre del mes y rentabilidad), usuarios para que entren al portal
// a cargar pedidos, y qué funciones ven. Antes cada cosa estaba en un lugar distinto (Usuarios, el menú
// del cliente, Rentabilidad) y no se sabía qué faltaba.
const INPUT = 'w-full h-11 px-3 rounded-xl bg-chip text-ink text-body font-bold outline-none placeholder:text-muted';
const sugerirUsuario = (nombre) => String(nombre || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);

function Paso({ hecho, titulo, texto, children }) {
    return (
        <div className="p-3.5 rounded-2xl bg-panel space-y-2.5">
            <div className="flex items-start gap-2.5">
                {hecho ? <LuCircleCheck size={20} className="shrink-0 text-[color:var(--etapa-listo)]" /> : <LuCircle size={20} className="shrink-0 text-muted" />}
                <div className="min-w-0">
                    <p className="text-body font-black text-ink">{titulo}</p>
                    <p className="text-caption text-muted leading-snug">{texto}</p>
                </div>
            </div>
            {children}
        </div>
    );
}

export default function EmpresaPortalSheet({ cliente, onCerrar, onTarifa, onFunciones, onRentabilidad, onCambio }) {
    const [usuarios, setUsuarios] = useState(null);
    const [form, setForm] = useState(null);   // { nombre, username, password } mientras se crea uno
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const [vinc, todos] = await Promise.all([api.get('/pedidos-empresa/usuarios'), api.get('/admin/usuarios')]);
            const ids = new Set((vinc.data || []).filter(x => x.clienteId === cliente.id).map(x => x.id));
            const sedes = Object.fromEntries((vinc.data || []).map(x => [x.id, x.sedeNombre]));
            setUsuarios((todos.data || []).filter(u => ids.has(u.id)).map(u => ({ ...u, sedeNombre: sedes[u.id] })));
        } catch { setUsuarios([]); }
    }, [cliente.id]);
    useEffect(() => { cargar(); }, [cargar]);

    const crear = async () => {
        const nombre = form.nombre.trim(), username = form.username.trim();
        if (!nombre || !username) { toast.error('Completá el nombre y el usuario'); return; }
        if (form.password.length < 6) { toast.error('La contraseña tiene que tener al menos 6 caracteres'); return; }
        setGuardando(true);
        try {
            await crearUsuario({ nombre, username, password: form.password, rol: 'EMPRESA', clienteId: cliente.id, sedeId: null, telefono: null, whatsapp: null });
            toast.success(`Listo: ${nombre} ya puede entrar con "${username}"`, { duration: 6000 });
            setForm(null);
            cargar();
            onCambio?.();
        } catch (e) {
            toast.error(e?.response?.status === 409 ? 'Ese usuario ya existe: elegí otro' : (e?.response?.data?.mensaje || 'No se pudo crear'));
        } finally { setGuardando(false); }
    };

    const fila = 'w-full h-11 px-3 rounded-xl bg-card text-ink text-label font-black inline-flex items-center justify-between active:scale-[0.99]';
    return (
        <ModalShell titulo="Empresa con portal" subtitulo={cliente.nombre} onCerrar={onCerrar}>
            <div className="space-y-2.5">
                <p className="text-caption text-secondary">Lo mismo que tiene Modo Agua: cargan sus pedidos en el portal, vos los agendás, y a fin de mes cerrás con la tarifa por cantidad de equipos.</p>

                <Paso hecho={!!cliente.tieneTarifa} titulo="Tarifa mensual por tramos"
                    texto={cliente.tieneTarifa ? 'Configurada: sus trabajos van sin precio y se cobran en el cierre del mes.' : 'El precio por equipo según cuántos se hagan en el mes (ej. de 40 a 59: $33.000 cada uno).'}>
                    <button type="button" onClick={onTarifa} className={fila}>{cliente.tieneTarifa ? 'Ver el cierre y la tarifa' : 'Configurar la tarifa'} <LuChevronRight size={16} /></button>
                </Paso>

                <Paso hecho={!!usuarios?.length} titulo="Usuarios del portal"
                    texto="Con este usuario entran a gestiondlt.com y cargan pedidos, ven el estado y comentan. No ven precios internos ni teléfonos.">
                    {usuarios === null ? <div className="h-11 rounded-xl bg-card animate-pulse" /> : (
                        <div className="space-y-1.5">
                            {usuarios.map(u => (
                                <div key={u.id} className="px-3 py-2 rounded-xl bg-card flex items-center justify-between gap-2">
                                    <span className="min-w-0">
                                        <span className="block text-body font-black text-ink truncate">{u.nombre}</span>
                                        <span className="block text-caption text-muted truncate">Usuario: {u.username}{u.sedeNombre ? ` · solo ${u.sedeNombre}` : ''}{u.activo === false ? ' · desactivado' : ''}</span>
                                    </span>
                                </div>
                            ))}
                            {form ? (
                                <div className="p-3 rounded-xl bg-card space-y-2">
                                    <input autoFocus value={form.nombre} placeholder="Nombre (ej. Rami)" className={INPUT}
                                        onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
                                    <input value={form.username} placeholder="Usuario para entrar" className={INPUT} autoCapitalize="none"
                                        onChange={e => setForm(f => ({ ...f, username: e.target.value, tocado: true }))} />
                                    <input value={form.password} placeholder="Contraseña (mínimo 6)" className={INPUT} type="text" autoCapitalize="none"
                                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                                    <p className="text-label text-muted">Pasale el usuario y la contraseña por WhatsApp. Puede cambiarla después.</p>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setForm(null)} className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black">Cancelar</button>
                                        <button type="button" disabled={guardando} onClick={crear} className="flex-[2] h-10 rounded-xl bg-[#C9341F] text-white text-label font-black disabled:opacity-40">{guardando ? 'Creando…' : 'Crear usuario'}</button>
                                    </div>
                                </div>
                            ) : (
                                <button type="button" onClick={() => setForm({ nombre: '', username: sugerirUsuario(cliente.nombre), password: '', tocado: false })} className={fila}>
                                    <span className="inline-flex items-center gap-1.5"><LuUserPlus size={15} /> {usuarios.length ? 'Agregar otro usuario' : 'Crear el usuario del portal'}</span><LuChevronRight size={16} />
                                </button>
                            )}
                        </div>
                    )}
                </Paso>

                <Paso hecho titulo="Qué ven en el portal" texto="Comentarios, reporte semanal, mapa y ruta del día. Lo sensible viene apagado.">
                    <button type="button" onClick={onFunciones} className={fila}>Elegir funciones <LuChevronRight size={16} /></button>
                </Paso>

                {cliente.tieneTarifa && (
                    <Paso hecho titulo="Rentabilidad" texto="Cuánto te deja por mes, día por día, y si conviene pasar trabajos al mes siguiente por el tramo.">
                        <button type="button" onClick={onRentabilidad} className={fila}>Ver la rentabilidad <LuChevronRight size={16} /></button>
                    </Paso>
                )}
            </div>
        </ModalShell>
    );
}
